import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AskAbout, InputPlaceholder } from 'autogen/translation-keys/trans-website-comai';
import { ButtonWithIcon } from 'components/Button';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import FlexRow from 'components/Layout/components/Flex/components/FlexRow';
import format from 'helpers/translations';
import { getProductName, sendMessage } from '../shared/helpers';
import useComAIStore, { useIsComAILoading } from '../store/comAIStore';
import AutoExpandTextArea from './AutoExpandTextArea';
import Chip from './Chip';

const TEMP_ICON_SEND_BUTTON = 'ArrowUp';
const TEMP_BOX_SHADOW = '0px 6px 12px 2px #0000001F';

const Wrapper = styled(FlexColumn)`
    box-shadow: ${TEMP_BOX_SHADOW};
    width: 100%;
    border-top-left-radius: ${({ theme }) => theme.RadiusLarge};
    border-top-right-radius: ${({ theme }) => theme.RadiusLarge};
`;

const ChipsRow = styled(FlexRow)`
    padding: ${({ theme }) => theme.Spacing16};
    padding-bottom: 0;
    gap: ${({ theme }) => theme.Spacing8};
`;

const Form = styled.form`
    display: flex;
    gap: ${({ theme }) => theme.Spacing8};
`;

const StyledButtonWithIcon = styled(ButtonWithIcon)`
    margin-bottom: ${({ theme }) => theme.Spacing16};
    margin-right: ${({ theme }) => theme.Spacing16};
    align-self: flex-end;
    flex-shrink: 0;
`;

export default function Footer() {
    const isLoading = useIsComAILoading();
    const inputValue = useComAIStore((state) => state.inputValue);
    const setInputValue = useComAIStore((state) => state.setInputValue);
    const initialData = useComAIStore((state) => state.initialData);
    const askMoreProduct = useComAIStore((state) => state.askMoreProduct);
    const setAskMoreProduct = useComAIStore((state) => state.setAskMoreProduct);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const { t } = useTranslation();

    function handleSendMessage(e: React.FormEvent) {
        e.preventDefault();

        if (!initialData || !inputValue || isLoading) {
            return;
        }

        if (!inputValue.trim()) {
            return;
        }
        const productId = askMoreProduct?.id?.toString();
        const productName = askMoreProduct?.listName && getProductName(askMoreProduct.listName);
        const productObj = productId && productName ? { product_id: productId, product_name: productName } : undefined;
        sendMessage(inputValue, productObj);
        setAskMoreProduct(null);
        setInputValue('');
    }

    useEffect(() => {
        if (askMoreProduct) {
            inputRef.current?.focus();
        }
    }, [askMoreProduct]);

    return (
        <Wrapper gap="0px">
            {!!askMoreProduct && (
                <ChipsRow>
                    <Chip onClick={() => setAskMoreProduct(null)} showCloseIcon ellipsis>
                        {format(t(AskAbout), getProductName(askMoreProduct.listName))}
                    </Chip>
                </ChipsRow>
            )}
            <Form
                onSubmit={handleSendMessage}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage(e)}
            >
                <AutoExpandTextArea
                    ref={inputRef}
                    autoFocus
                    id="COMAIInput"
                    placeholder={t(InputPlaceholder)}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                />
                <StyledButtonWithIcon
                    variant="primary"
                    icon={TEMP_ICON_SEND_BUTTON}
                    size="md"
                    disabled={!initialData || !inputValue || isLoading}
                />
            </Form>
        </Wrapper>
    );
}
